import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetInteractionTrendsQuery } from "../../../../redux/service/modules/sales";
import { Box } from "@mui/material";
import CustomToggle from "../../../../components/common/CustomToggle";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    Legend,
} from "recharts";
import {
    getAnalysts,
    getColors,
    transformChartData,
    transformDataByMetric,
    transformDataByName,
} from "./chartTransformer";
import Loader from "../../../../components/common/loader";
import NoDataAvailable from "../../../../components/common/NoDataAvailable";
import { useSelector } from "react-redux";
import { useResponsiveStyles } from "../../../../styles/useResponsiveStyles";
import { CommonXAxisTiltTick } from "../../../../components/common/CommonTiltTick";

export const InteractionChart = () => {
    const dateRange = useSelector((state) => state.date);
    const { getFontSize } = useResponsiveStyles();
    const { id } = useParams();
    const {
        data: interactionTrends,
        isFetching,
        error,
    } = useGetInteractionTrendsQuery({
        id,
        start_date: dateRange?.startDate,
        end_date: dateRange?.endDate,
    });
    const [selectedValue, setSelectedValue] = useState("Analyst");
    const [transformedData, setTransformedData] = useState([]);
    const [analysts, setAnalysts] = useState([]);

    useEffect(() => {
        if (interactionTrends) {
            if (selectedValue === "Analyst") {
                const data = transformDataByMetric(interactionTrends);
                setTransformedData(transformChartData(data));
                setAnalysts(getAnalysts(data));
            } else {
                const data = transformDataByName(interactionTrends);
                setTransformedData(transformChartData(data));
                setAnalysts(getAnalysts(data));
            }
        }
    }, [interactionTrends, selectedValue]);

    const onChange = (val) => {
        setSelectedValue(val);
    };

    let staticContent;
    if (isFetching) {
        staticContent = <Loader />;
    } else if (error) {
        staticContent = "Error";
    } else if (transformedData.length === 0) {
        staticContent = <NoDataAvailable />;
    }

    return (
        <Box>
            <>
                <div className="flex justify-between mt-10">
                    <Box>
                        <h1
                            className="font-bold text-primary"
                            style={{ ...getFontSize("heading") }}
                        >
                            Client Interaction
                        </h1>
                    </Box>
                    <Box>
                        <CustomToggle
                            options={["Analyst", "Interactions"]}
                            onChange={onChange}
                            selectedValue={selectedValue}
                        />
                    </Box>
                </div>
                {staticContent ? (
                    <Box className="h-[calc(100vh-400px)]">
                        <div style={styles.staticContent}>{staticContent}</div>
                    </Box>
                ) : (
                    <div className="h-[100vh]">
                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                            className={"mt-8"}
                        >
                            <BarChart
                                layout="horizontal" // Key change: set layout to vertical
                                data={transformedData}
                                margin={{
                                    top: 15,
                                    right: 30,
                                    left: 40,
                                    bottom: 120,
                                }}
                                barSize={30}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    horizontal={true}
                                    vertical={false}
                                />
                                <YAxis type="number" />
                                <XAxis
                                    dataKey="activityType"
                                    tick={(props) => (
                                        <CommonXAxisTiltTick {...props} />
                                    )}
                                    interval={0}
                                />
                                <Tooltip
                                    cursor={{ fill: "transparent" }}
                                    content={({ payload, label }) => {
                                        if (payload && payload.length) {
                                            const totalInteraction =
                                                payload.reduce(
                                                    (sum, entry) =>
                                                        entry.name !==
                                                        "Total interaction"
                                                            ? sum +
                                                              (entry.value || 0)
                                                            : sum,
                                                    0
                                                );

                                            const nonZeroValues = payload
                                                .filter(
                                                    (entry) =>
                                                        entry.value > 0 &&
                                                        entry.name !==
                                                            "Total interaction"
                                                )
                                                .sort(
                                                    (a, b) => b.value - a.value
                                                );

                                            return (
                                                <div className="bg-white p-2 shadow">
                                                    <div className="font-bold mb-2">
                                                        {label}
                                                    </div>
                                                    {nonZeroValues.map(
                                                        (entry) => (
                                                            <div
                                                                key={entry.name}
                                                                className="flex justify-between"
                                                            >
                                                                <div className="mr-3">
                                                                    {entry.name
                                                                        .split(
                                                                            "_"
                                                                        )
                                                                        .map(
                                                                            (
                                                                                word
                                                                            ) =>
                                                                                word
                                                                                    .charAt(
                                                                                        0
                                                                                    )
                                                                                    .toUpperCase() +
                                                                                word
                                                                                    .slice(
                                                                                        1
                                                                                    )
                                                                                    .toLowerCase()
                                                                        )
                                                                        .join(
                                                                            " "
                                                                        )}
                                                                </div>
                                                                <div>
                                                                    {
                                                                        entry.value
                                                                    }
                                                                </div>
                                                            </div>
                                                        )
                                                    )}
                                                    <div className="border-t mt-2 pt-1 font-bold flex justify-between">
                                                        <span>Total</span>
                                                        <span>
                                                            {totalInteraction}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return null;
                                    }}
                                />
                                <Legend
                                    layout="horizontal"
                                    iconType="circle"
                                    verticalAlign="bottom"
                                    align="center"
                                    wrapperStyle={{
                                        paddingTop: "50px",
                                        ...getFontSize("body1"),
                                    }}
                                    formatter={(value) => {
                                        return (
                                            <span
                                                style={{
                                                    color: "black",
                                                    marginRight: 15,
                                                }}
                                            >
                                                {value
                                                    .split("_")
                                                    .map(
                                                        (word) =>
                                                            word
                                                                .charAt(0)
                                                                .toUpperCase() +
                                                            word
                                                                .slice(1)
                                                                .toLowerCase()
                                                    )
                                                    .join(" ")}
                                            </span>
                                        );
                                    }}
                                />
                                {analysts.map((analyst, index) => (
                                    <Bar
                                        key={analyst}
                                        dataKey={analyst}
                                        stackId="a"
                                        fill={getColors(analysts.length)[index]}
                                        name={analyst}
                                        layout="vertical"
                                        barSize={30}
                                        radius={
                                            analysts.length - 1 === index && [
                                                0, 6, 6, 0,
                                            ]
                                        }
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </>
        </Box>
    );
};

const styles = {
    staticContent: {
        height: "80%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
};
